import axios from "axios";
import { BaseResponse } from "../models/base";

// Status configuration types
export interface StatusConfig {
  fieldKey: string;
  label: string;
  order: number;
  isVisible: boolean;
  _id?: string;
}

export interface StatusConfigResponse {
  statusConfig: StatusConfig[];
}

// Application filters
export interface ApplicationFilters {
  jobName?: string[];
  status?: string[];
  location?: string[];
  minYears?: number;
  maxYears?: number;
  jobId?: string;
  companyId?: string;
  candidateName?: string;
  page?: number;
  limit?: number;
  hasViewed?: 'viewed' | 'not_viewed';
}

// 1. Get Applicants Status Configuration
// GET /clients/job/applicants/status-config/{jobId}
export const getApplicantStatusConfig = async (
  jobId: string
): Promise<BaseResponse<StatusConfigResponse> | undefined> => {
  try {
    const response = await axios.get(
      `${
        import.meta.env.VITE_BASE_URL
      }/clients/job/applicants/status-config/${jobId}`,
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );

    // Normalize the response to ensure totalApplicants shows as "Applied"
    if (response.data?.data?.statusConfig) {
      const normalizedConfig = response.data.data.statusConfig.map(
        (config: StatusConfig) => ({
          ...config,
          label:
            config.fieldKey === "totalApplicants" &&
            config.label === "Total Applicants"
              ? "Applied"
              : config.label,
        })
      );

      return {
        ...response.data,
        data: {
          ...response.data.data,
          statusConfig: normalizedConfig,
        },
      };
    }

    return response.data;
  } catch (error) {
    console.error("Error getting status config:", error);
    return undefined;
  }
};

// 2. Update Applicants Status Configuration
// POST /clients/job/applicants/status-config/update/{jobId}
export const updateApplicantStatusConfig = async (
  jobId: string,
  statusConfig: StatusConfig[]
): Promise<BaseResponse<boolean> | undefined> => {
  try {
    // Validate all 7 required fields are present
    const requiredFields = [
      "onHold",
      "screenings",
      "interviews",
      "shortlisted",
      "offered",
      "rejected",
      "hired",
    ];
    const providedFields = statusConfig.map((config) => config.fieldKey);
    const hasAllFields = requiredFields.every((field) =>
      providedFields.includes(field)
    );

    if (!hasAllFields) {
      console.error("Missing required status fields");
      return undefined;
    }

    const response = await axios.post(
      `${
        import.meta.env.VITE_BASE_URL
      }/clients/job/applicants/status-config/update/${jobId}`,
      { statusConfig },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating status config:", error);
    return undefined;
  }
};

// 3. Mark Application as Viewed
// POST /candidate/jobs/applications/{applicationId}/mark-viewed
export const markApplicationViewed = async (
  applicationId: string
): Promise<
  BaseResponse<{ success: boolean; viewedAt: string }> | undefined
> => {
  try {
    const response = await axios.post(
      `${
        import.meta.env.VITE_BASE_URL
      }/candidate/jobs/applications/${applicationId}/mark-viewed`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error marking application as viewed:", error);
    return undefined;
  }
};

// 4. Bulk Update Application Status
// POST /candidate/jobs/applicants/status/bulk-update
export const bulkUpdateApplicantStatus = async (
  applicationIds: string[],
  newStatus: string
): Promise<BaseResponse<boolean> | undefined> => {
  try {
    // Normalize status names for API compatibility
    let normalizedStatus = newStatus;

    // Map plural forms to singular forms that the API expects
    switch (newStatus) {
      case "totalApplicants":
        normalizedStatus = "applied";
        break;
      case "interviews":
        normalizedStatus = "interview";
        break;
      case "screenings":
        normalizedStatus = "screening";
        break;
      // Other statuses remain as is (shortlisted, offered, hired, rejected, onHold)
      default:
        normalizedStatus = newStatus;
    }

    const response = await axios.post(
      `${
        import.meta.env.VITE_BASE_URL
      }/candidate/jobs/applicants/status/bulk-update`,
      {
        applicationIds,
        newStatus: normalizedStatus,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("userId"),
          "x-profileid": localStorage.getItem("profileId") || "", // As per API doc
          
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error bulk updating status:", error);
    return undefined;
  }
};

// 5. Get Job Applications
// Using existing endpoint with same structure as Applications.tsx
export const getJobApplications = async (
  jobName: string,
  filters?: ApplicationFilters
): Promise<any> => {
  try {
    // Transform status filter values to match API expectations
    const transformedStatus = filters?.status
      ? filters.status.map((status) => {
          switch (status) {
            case "screenings":
              return "screening";
            case "interviews":
              return "interview";
            default:
              return status;
          }
        })
      : [];

    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/candidate/jobs/applications/all/job`,
      {
        page: filters?.page || 1,
        limit: filters?.limit || 200,
        filters: {
          jobName: [jobName],
          status: transformedStatus,
          location: filters?.location || undefined,
          minYears: filters?.minYears || undefined,
          maxYears: filters?.maxYears || undefined,
          jobId: filters?.jobId || undefined,
          companyId: localStorage.getItem("companyId"),
          searchQuery: filters?.candidateName,
          hasViewed: filters?.hasViewed || undefined,
        },
      },
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting job applications:", error);
    return undefined;
  }
};

// Check profile access
export const checkProfileAccess = async (
  companyId: string,
  profileId: string
): Promise<{ hasAccessed: boolean } | undefined> => {
  try {
    const response = await axios.post(
      `${import.meta.env.VITE_BASE_URL}/candidate/profile/check-access`,
      {
        companyId,
        profileId,
      },
      {
        headers: {
          Authorization: localStorage.getItem("userId"),
        },
      }
    );

    // Return the data object which contains hasAccessed
    return response.data?.data;
  } catch (error) {
    console.error("Error checking profile access:", error);
    return undefined;
  }
};

// Helper function to get default funnel configuration
export const getDefaultFunnelConfig = (): StatusConfig[] => {
  return [
    { fieldKey: "onHold", label: "On Hold", order: 1, isVisible: true },
    { fieldKey: "screenings", label: "Screening", order: 2, isVisible: true },
    { fieldKey: "interviews", label: "Interview", order: 3, isVisible: true },
    {
      fieldKey: "shortlisted",
      label: "Shortlisted",
      order: 4,
      isVisible: true,
    },
    { fieldKey: "offered", label: "Offered", order: 5, isVisible: true },
    { fieldKey: "hired", label: "Hired", order: 6, isVisible: true },
    { fieldKey: "rejected", label: "Rejected", order: 7, isVisible: true },
  ];
};
